import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Search, ChevronLeft, ChevronRight, Calendar, Eye, User, FolderOpen, FileText, Zap, ArrowRight, Filter, Sparkles, ShieldCheck, Phone, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { articleApi } from '@/user/api';
import type { ArticleVO } from '@/types/api-entity';

// ============== 滚动动画 Hook ==============
function useScrollReveal() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(el);
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { ref, visible };
}

function ScrollReveal({ children, className = '', delay = 0 }: { children: React.ReactNode; className?: string; delay?: number }) {
  const { ref, visible } = useScrollReveal();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(32px)',
        transition: `all 600ms cubic-bezier(0.16, 1, 0.3, 1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

export default function Articles() {
  const [articles, setArticles] = useState<ArticleVO[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [keyword, setKeyword] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [pageInfo, setPageInfo] = useState({
    pageNum: 1,
    pageSize: 9,
    total: 0,
    pages: 0,
  });

  // 加载分类列表
  const loadCategories = async () => {
    try {
      const result = await articleApi.getCategories();
      if (result.code === 200) {
        setCategories(result.data);
      }
    } catch (err) {
      console.error('加载分类失败:', err);
    }
  };

  // 加载文章列表
  const loadArticles = async (pageNum = 1) => {
    setLoading(true);
    try {
      const result = await articleApi.getArticleList({
        pageNum,
        pageSize: pageInfo.pageSize,
        category: selectedCategory || undefined,
        keyword: keyword || undefined,
      });

      if (result.code === 200) {
        setArticles(result.data.list);
        setPageInfo({
          pageNum: result.data.pageNum,
          pageSize: result.data.pageSize,
          total: result.data.total,
          pages: result.data.pages,
        });
      }
    } catch (err) {
      console.error('加载文章失败:', err);
    } finally {
      setLoading(false);
    }
  };

  // 搜索
  const handleSearch = () => {
    loadArticles(1);
  };

  // 切换分类
  const handleCategoryChange = (category: string) => {
    setSelectedCategory(category === selectedCategory ? '' : category);
    loadArticles(1);
  };

  // 切换页码
  const handlePageChange = (pageNum: number) => {
    if (pageNum >= 1 && pageNum <= pageInfo.pages) {
      loadArticles(pageNum);
    }
  };

  // 格式化日期
  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('zh-CN');
  };

  useEffect(() => {
    loadCategories();
    loadArticles();
  }, []);

  // 当分类改变时重新加载
  useEffect(() => {
    loadArticles(1);
  }, [selectedCategory]);

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-gray-50 to-white">
      <Header />
      <main className="flex-1 pt-20">
        {/* ========== Hero 区域 ========== */}
        <section className="relative py-20 md:py-28 overflow-hidden">
          {/* 背景装饰 */}
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute top-20 right-20 w-96 h-96 bg-[#1c5bff]/5 rounded-full blur-3xl animate-blob" />
            <div className="absolute bottom-20 left-20 w-80 h-80 bg-purple-100 rounded-full blur-3xl animate-blob animation-delay-2000" />
          </div>

          {/* 装饰圆环 */}
          <div className="absolute top-1/4 right-1/4 w-64 h-64 border border-gray-100 rounded-full" />
          <div className="absolute bottom-1/4 left-1/4 w-48 h-48 border border-gray-100 rounded-full" />

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <ScrollReveal className="text-center">
              {/* 标签 */}
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1c5bff]/10 text-[#1c5bff] rounded-full text-sm font-medium mb-6">
                <Sparkles className="w-4 h-4" />
                资讯中心
              </div>

              {/* 主标题 */}
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
                了解最新动态
                <br />
                <span className="text-[#1c5bff] text-3xl">掌握行业资讯</span>
              </h1>

              {/* 描述 */}
              <p className="text-gray-600 text-lg max-w-2xl mx-auto mb-8">
                汇聚行业动态、政策解读、市场分析等优质内容，助力企业把握市场脉搏
              </p>

              {/* 特性标签 */}
              <div className="flex flex-wrap justify-center gap-3">
                {['行业资讯', '政策解读', '市场分析', '专家观点'].map((tag, index) => (
                  <div key={index} className="flex items-center gap-1.5 px-3 py-1.5 bg-white border border-gray-200 rounded-lg text-sm text-gray-600 shadow-sm">
                    <FileText className="w-3.5 h-3.5 text-[#1c5bff]" />
                    {tag}
                  </div>
                ))}
              </div>
            </ScrollReveal>
          </div>

          {/* 底部波浪装饰 */}
          <div className="absolute bottom-0 left-0 w-full">
            <svg viewBox="0 0 1440 120" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M0 120L48 110C96 100 192 80 288 70C384 60 480 60 576 65C672 70 768 80 864 85C960 90 1056 90 1152 85C1248 80 1344 70 1392 65L1440 60V120H1392C1344 120 1248 120 1152 120C1056 120 960 120 864 120C768 120 672 120 576 120C480 120 384 120 288 120C192 120 96 120 48 120H0Z" fill="white"/>
            </svg>
          </div>
        </section>

        {/* ========== 搜索和筛选区域 ========== */}
        <section className="py-12 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <ScrollReveal delay={100}>
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 md:p-8 relative overflow-hidden">
                {/* 背景装饰 */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-[#1c5bff]/5 to-transparent rounded-bl-3xl" />

                {/* 搜索区域 */}
                <div className="flex flex-col md:flex-row gap-4 mb-6">
                  <div className="flex-1 relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <Input
                      type="text"
                      placeholder="搜索文章标题或内容..."
                      value={keyword}
                      onChange={(e) => setKeyword(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                      className="pl-12 h-12 border-2 border-gray-200 focus:border-[#1c5bff] transition-colors"
                    />
                  </div>
                  <Button 
                    onClick={handleSearch}
                    className="h-12 px-8 bg-[#1c5bff] text-white hover:bg-[#1547cc] transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                  >
                    <Search className="w-4 h-4 mr-2" />
                    搜索
                  </Button>
                </div>

                {/* 分类筛选 */}
                {categories.length > 0 && (
                  <div className="pt-6 border-t border-gray-100">
                    <div className="flex items-center gap-3 flex-wrap">
                      <div className="flex items-center gap-2 text-gray-500 text-sm">
                        <Filter className="w-4 h-4" />
                        <span className="font-medium">分类筛选：</span>
                      </div>
                      <button
                        onClick={() => handleCategoryChange('')}
                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                          selectedCategory === ''
                            ? 'bg-[#1c5bff] text-white shadow-lg'
                            : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                        }`}
                      >
                        全部
                      </button>
                      {categories.map((category) => (
                        <button
                          key={category}
                          onClick={() => handleCategoryChange(category)}
                          className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                            selectedCategory === category
                              ? 'bg-[#1c5bff] text-white shadow-lg'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {category}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* ========== 文章列表区域 ========== */}
        <section className="py-12 bg-gray-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            {loading ? (
              <div className="text-center py-20">
                <div className="inline-block w-12 h-12 border-4 border-gray-200 border-t-[#1c5bff] rounded-full animate-spin" />
                <p className="mt-4 text-gray-500">加载中...</p>
              </div>
            ) : articles.length === 0 ? (
              <ScrollReveal>
                <div className="text-center py-20 bg-white rounded-2xl shadow-sm border border-gray-100">
                  <div className="text-6xl mb-4">📄</div>
                  <h3 className="text-xl font-semibold text-gray-700 mb-2">暂无文章</h3>
                  <p className="text-gray-500">暂无相关文章内容</p>
                </div>
              </ScrollReveal>
            ) : (
              <>
                <ScrollReveal delay={200}>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {articles.map((article, index) => (
                      <Link
                        key={article.id}
                        to={`/article/${article.id}`}
                        className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-500 border border-gray-100 hover:border-[#1c5bff]/30"
                        style={{ transitionDelay: `${index * 100}ms` }}
                      >
                        {/* 封面图片 */}
                        <div className="relative aspect-video overflow-hidden bg-gray-100">
                          <img
                            src={article.coverImage || 'https://picsum.photos/400/225'}
                            alt={article.title}
                            className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                          
                          {/* 分类标签 */}
                          {article.category && (
                            <div className="absolute top-4 left-4">
                              <span className="inline-block px-3 py-1 bg-[#1c5bff] text-white text-xs font-medium rounded-full shadow-lg">
                                {article.category}
                              </span>
                            </div>
                          )}
                        </div>

                        {/* 内容区域 */}
                        <div className="p-6">
                          {/* 标题 */}
                          <h3 className="text-lg font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-[#1c5bff] transition-colors">
                            {article.title}
                          </h3>

                          {/* 摘要 */}
                          <p className="text-gray-500 text-sm line-clamp-2 mb-4 leading-relaxed">
                            {article.summary || '暂无摘要'}
                          </p>

                          {/* 元信息 */}
                          <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                            <div className="flex items-center gap-4 text-xs text-gray-400">
                              <span className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {article.authorName || '匿名'}
                              </span>
                              <span className="flex items-center gap-1">
                                <Eye className="w-3 h-3" />
                                {article.viewCount}
                              </span>
                            </div>
                            <span className="flex items-center gap-1 text-xs text-gray-400">
                              <Calendar className="w-3 h-3" />
                              {formatDate(article.publishTime)}
                            </span>
                          </div>

                          {/* 阅读更多 */}
                          <div className="mt-4 flex items-center gap-2 text-sm font-medium text-[#1c5bff] opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                            阅读更多
                            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </ScrollReveal>

                {/* 分页 */}
                {pageInfo.pages > 1 && (
                  <ScrollReveal delay={300}>
                    <div className="flex justify-center items-center gap-2 mt-12">
                      <button
                        onClick={() => handlePageChange(pageInfo.pageNum - 1)}
                        disabled={pageInfo.pageNum <= 1}
                        className="w-10 h-10 rounded-lg border border-gray-200 hover:border-[#1c5bff] hover:text-[#1c5bff] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>

                      {Array.from({ length: pageInfo.pages }, (_, i) => i + 1)
                        .filter((page) => {
                          const current = pageInfo.pageNum;
                          return (
                            page === 1 ||
                            page === pageInfo.pages ||
                            (page >= current - 2 && page <= current + 2)
                          );
                        })
                        .map((page, index, arr) => (
                          <div key={page} className="flex items-center">
                            {index > 0 && arr[index - 1] !== page - 1 && (
                              <span className="px-2 text-gray-400">...</span>
                            )}
                            <button
                              onClick={() => handlePageChange(page)}
                              className={`w-10 h-10 rounded-lg text-sm font-medium transition-all duration-300 ${
                                page === pageInfo.pageNum
                                  ? 'bg-[#1c5bff] text-white shadow-lg'
                                  : 'border border-gray-200 hover:border-[#1c5bff] hover:text-[#1c5bff]'
                              }`}
                            >
                              {page}
                            </button>
                          </div>
                        ))}

                      <button
                        onClick={() => handlePageChange(pageInfo.pageNum + 1)}
                        disabled={pageInfo.pageNum >= pageInfo.pages}
                        className="w-10 h-10 rounded-lg border border-gray-200 hover:border-[#1c5bff] hover:text-[#1c5bff] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                    </div>
                  </ScrollReveal>
                )}

                {/* 页面信息 */}
                <div className="text-center mt-4 text-sm text-gray-400">
                  共 {pageInfo.total} 条记录，第 {pageInfo.pageNum}/{pageInfo.pages} 页
                </div>
              </>
            )}
          </div>
        </section>

      </main>
      <Footer />

      {/* 自定义动画样式 */}
      <style>{`
        @keyframes blob {
          0% { transform: translate(0px, 0px) scale(1); }
          33% { transform: translate(30px, -50px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
          100% { transform: translate(0px, 0px) scale(1); }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
      `}</style>
    </div>
  );
}